/**
 * Agent Selector Component
 * Dropdown to select active agent in chat interface
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useAgent } from '@/context/AgentContext';
import type { Agent } from '@/types/agent.types';

interface AgentSelectorProps {
  compact?: boolean;
}

export function AgentSelector({ compact = false }: AgentSelectorProps) {
  const { agents, selectedAgent, selectAgent } = useAgent();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const activeAgents = agents.filter((agent) => agent.status === 'active');

  const handleSelectAgent = useCallback((agent: Agent) => {
    selectAgent(agent.id);
    setIsOpen(false);
    buttonRef.current?.focus();
  }, [selectAgent]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) =>
            prev < activeAgents.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < activeAgents.length) {
            handleSelectAgent(activeAgents[focusedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          buttonRef.current?.focus();
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(activeAgents.length - 1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, focusedIndex, activeAgents, handleSelectAgent]);

  // Reset focused index when opening
  useEffect(() => {
    if (isOpen) {
      const selectedIndex = activeAgents.findIndex(
        (a) => a.id === selectedAgent?.id
      );
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, activeAgents, selectedAgent]);

  if (activeAgents.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 rounded-lg border border-border-default bg-bg-primary px-3 py-1.5 text-sm hover:border-border-medium transition-colors"
        >
          <span>{selectedAgent?.icon || '🤖'}</span>
          <ChevronDown className="h-4 w-4 text-text-tertiary" />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-border-default bg-bg-primary py-1 shadow-lg">
            {activeAgents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => handleSelectAgent(agent)}
                className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-bg-secondary"
              >
                <span className="text-xl">{agent.icon || '🤖'}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-text-primary">
                      {agent.name}
                    </span>
                    {agent.id === selectedAgent?.id && (
                      <Check className="h-4 w-4 text-stellar-primary" />
                    )}
                  </div>
                  <span className="text-xs text-text-tertiary line-clamp-1">
                    {agent.description}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={
          'flex w-full items-center justify-between gap-3 rounded-lg ' +
          'border border-border-default bg-bg-primary px-4 py-3 ' +
          'transition-colors hover:border-border-medium ' +
          'focus-visible:outline-none focus-visible:ring-2 ' +
          'focus-visible:ring-stellar-primary focus-visible:ring-offset-2'
        }
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select agent"
        aria-controls="agent-listbox"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="text-2xl flex-shrink-0">
            {selectedAgent?.icon || '🤖'}
          </span>
          <div className="min-w-0 flex-1 text-left">
            <div className="font-medium text-text-primary truncate">
              {selectedAgent?.name || 'Select Agent'}
            </div>
            <div className="text-sm text-text-tertiary truncate">
              {selectedAgent?.description || 'No agent selected'}
            </div>
          </div>
        </div>
        <ChevronDown
          className={
            `h-5 w-5 flex-shrink-0 text-text-tertiary ` +
            `transition-transform ${isOpen ? 'rotate-180' : ''}`
          }
        />
      </button>

      {isOpen && (
        <div
          id="agent-listbox"
          role="listbox"
          aria-label="Available agents"
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded-lg border border-border-default bg-bg-primary py-1 shadow-lg"
        >
          {activeAgents.map((agent, index) => (
            <button
              key={agent.id}
              role="option"
              aria-selected={agent.id === selectedAgent?.id}
              onClick={() => handleSelectAgent(agent)}
              className={
                `flex w-full items-center gap-3 px-4 py-3 ` +
                `text-left transition-colors ` +
                `focus-visible:outline-none focus-visible:ring-2 ` +
                `focus-visible:ring-inset focus-visible:ring-stellar-primary ${
                  index === focusedIndex
                    ? 'bg-bg-secondary'
                    : agent.id === selectedAgent?.id
                      ? 'bg-bg-tertiary'
                      : 'hover:bg-bg-secondary'
                }`
              }
            >
              <span className="text-2xl flex-shrink-0">
                {agent.icon || '🤖'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-text-primary">
                    {agent.name}
                  </span>
                  {agent.isDefault && (
                    <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs font-medium text-text-primary">
                      Default
                    </span>
                  )}
                  {agent.id === selectedAgent?.id && (
                    <Check className="h-4 w-4 text-stellar-primary" />
                  )}
                </div>
                <div className="mt-1 text-sm text-text-secondary line-clamp-1">
                  {agent.description}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {agent.capabilities.slice(0, 3).map((cap) => (
                    <span
                      key={cap}
                      className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
